/**
 * HFDS — Headzflex Design System
 * Component: HF-005 Accordion System
 * Version: 1.0.0
 *
 * Responsibilities:
 * - Accessible accordion behavior.
 * - Optional single-open mode.
 * - Keyboard navigation.
 * - Shopify Theme Editor support.
 * - HFDS analytics events.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hf-accordion]";
  const ITEM_SELECTOR = "[data-hf-accordion-item]";
  const TRIGGER_SELECTOR = "[data-hf-accordion-trigger]";
  const PANEL_SELECTOR = "[data-hf-accordion-panel]";

  class HFAccordion {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;
      this.singleOpen = root.dataset.singleOpen === "true";
      this.allowAllClosed =
        root.dataset.allowAllClosed !== "false";

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.initialize();
    }

    initialize() {
      if (!window.HFDS) {
        console.error(
          "[HFDS Accordion] HFDS core is unavailable."
        );
        return;
      }

      this.root.addEventListener("click", this.handleClick);
      this.root.addEventListener(
        "keydown",
        this.handleKeydown
      );

      this.syncInitialState();
      this.root.dataset.hfInitialized = "true";
    }

    /**
     * @returns {HTMLElement[]}
     */
    getItems() {
      return window.HFDS
        .queryAll(ITEM_SELECTOR, this.root)
        .filter((item) => item instanceof HTMLElement);
    }

    /**
     * @returns {HTMLButtonElement[]}
     */
    getTriggers() {
      return window.HFDS
        .queryAll(TRIGGER_SELECTOR, this.root)
        .filter(
          (trigger) =>
            trigger instanceof HTMLButtonElement &&
            !trigger.disabled
        );
    }

    syncInitialState() {
      const items = this.getItems();
      let openFound = false;

      items.forEach((item) => {
        const trigger = item.querySelector(TRIGGER_SELECTOR);
        const panel = item.querySelector(PANEL_SELECTOR);

        if (
          !(trigger instanceof HTMLButtonElement) ||
          !(panel instanceof HTMLElement)
        ) {
          return;
        }

        const expanded =
          trigger.getAttribute("aria-expanded") === "true";

        if (this.singleOpen && expanded && openFound) {
          trigger.setAttribute("aria-expanded", "false");
          panel.hidden = true;
          item.classList.remove("is-open");
          return;
        }

        if (expanded) {
          openFound = true;
          panel.hidden = false;
          item.classList.add("is-open");
        } else {
          panel.hidden = true;
          item.classList.remove("is-open");
        }
      });

      if (
        !this.allowAllClosed &&
        !openFound &&
        items.length > 0
      ) {
        const firstTrigger =
          items[0].querySelector(TRIGGER_SELECTOR);

        if (firstTrigger instanceof HTMLButtonElement) {
          this.open(firstTrigger, {
            announce: false,
            emitEvent: false
          });
        }
      }
    }

    /**
     * @param {MouseEvent} event
     */
    handleClick(event) {
      const trigger = event.target.closest(TRIGGER_SELECTOR);

      if (
        !(trigger instanceof HTMLButtonElement) ||
        !this.root.contains(trigger) ||
        trigger.disabled
      ) {
        return;
      }

      this.toggle(trigger);
    }

    /**
     * @param {KeyboardEvent} event
     */
    handleKeydown(event) {
      const activeTrigger =
        event.target.closest(TRIGGER_SELECTOR);

      if (!(activeTrigger instanceof HTMLButtonElement)) {
        return;
      }

      const triggers = this.getTriggers();
      const currentIndex = triggers.indexOf(activeTrigger);

      if (currentIndex === -1) {
        return;
      }

      let nextIndex = null;

      if (event.key === "ArrowDown") {
        nextIndex = (currentIndex + 1) % triggers.length;
      }

      if (event.key === "ArrowUp") {
        nextIndex =
          (currentIndex - 1 + triggers.length) %
          triggers.length;
      }

      if (event.key === "Home") {
        nextIndex = 0;
      }

      if (event.key === "End") {
        nextIndex = triggers.length - 1;
      }

      if (nextIndex !== null) {
        event.preventDefault();
        triggers[nextIndex]?.focus();
      }
    }

    /**
     * @param {HTMLButtonElement} trigger
     */
    toggle(trigger) {
      const isExpanded =
        trigger.getAttribute("aria-expanded") === "true";

      if (isExpanded) {
        this.close(trigger);
      } else {
        this.open(trigger);
      }
    }

    /**
     * @param {HTMLButtonElement} trigger
     * @param {Object} options
     */
    open(
      trigger,
      {
        announce = true,
        emitEvent = true
      } = {}
    ) {
      if (this.singleOpen) {
        this.getTriggers().forEach((otherTrigger) => {
          if (
            otherTrigger !== trigger &&
            otherTrigger.getAttribute("aria-expanded") ===
              "true"
          ) {
            this.close(otherTrigger, {
              announce: false,
              emitEvent: false
            });
          }
        });
      }

      const item = trigger.closest(ITEM_SELECTOR);
      const panel = this.getPanel(trigger);

      if (
        !(item instanceof HTMLElement) ||
        !(panel instanceof HTMLElement)
      ) {
        return;
      }

      trigger.setAttribute("aria-expanded", "true");
      panel.hidden = false;
      item.classList.add("is-open");

      if (emitEvent) {
        window.HFDS.emit(
          "accordion:open",
          this.getEventDetail(trigger),
          this.root
        );
      }

      if (announce) {
        window.HFDS.announce(
          `${this.getTriggerLabel(trigger)} expanded.`,
          "polite"
        );
      }
    }

    /**
     * @param {HTMLButtonElement} trigger
     * @param {Object} options
     */
    close(
      trigger,
      {
        announce = true,
        emitEvent = true
      } = {}
    ) {
      if (
        !this.allowAllClosed &&
        this.getOpenTriggers().length === 1 &&
        trigger.getAttribute("aria-expanded") === "true"
      ) {
        return;
      }

      const item = trigger.closest(ITEM_SELECTOR);
      const panel = this.getPanel(trigger);

      if (
        !(item instanceof HTMLElement) ||
        !(panel instanceof HTMLElement)
      ) {
        return;
      }

      trigger.setAttribute("aria-expanded", "false");
      panel.hidden = true;
      item.classList.remove("is-open");

      if (emitEvent) {
        window.HFDS.emit(
          "accordion:close",
          this.getEventDetail(trigger),
          this.root
        );
      }

      if (announce) {
        window.HFDS.announce(
          `${this.getTriggerLabel(trigger)} collapsed.`,
          "polite"
        );
      }
    }

    /**
     * @returns {HTMLButtonElement[]}
     */
    getOpenTriggers() {
      return this.getTriggers().filter(
        (trigger) =>
          trigger.getAttribute("aria-expanded") === "true"
      );
    }

    /**
     * @param {HTMLButtonElement} trigger
     * @returns {HTMLElement|null}
     */
    getPanel(trigger) {
      const panelId = trigger.getAttribute("aria-controls");

      if (!panelId) {
        return null;
      }

      const panel = document.getElementById(panelId);

      return panel instanceof HTMLElement ? panel : null;
    }

    /**
     * @param {HTMLButtonElement} trigger
     * @returns {string}
     */
    getTriggerLabel(trigger) {
      return (
        trigger.dataset.accordionLabel ||
        trigger.textContent?.trim() ||
        "Section"
      );
    }

    /**
     * @param {HTMLButtonElement} trigger
     * @returns {Object}
     */
    getEventDetail(trigger) {
      const item = trigger.closest(ITEM_SELECTOR);

      return {
        accordionId: this.root.id || "",
        itemId: item?.id || "",
        triggerId: trigger.id || "",
        label: this.getTriggerLabel(trigger),
        expanded:
          trigger.getAttribute("aria-expanded") === "true"
      };
    }

    destroy() {
      this.root.removeEventListener(
        "click",
        this.handleClick
      );

      this.root.removeEventListener(
        "keydown",
        this.handleKeydown
      );

      delete this.root.dataset.hfInitialized;
    }
  }

  function initializeAccordions(context = document) {
    if (!window.HFDS) {
      return;
    }

    window.HFDS
      .queryAll(ROOT_SELECTOR, context)
      .forEach((root) => {
        if (!(root instanceof HTMLElement)) {
          return;
        }

        if (root.dataset.hfInitialized === "true") {
          return;
        }

        root.hfAccordion = new HFAccordion(root);
      });
  }

  window.HFDS?.ready(() => {
    initializeAccordions(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeAccordions(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      window.HFDS
        ?.queryAll(ROOT_SELECTOR, event.target)
        .forEach((root) => {
          root.hfAccordion?.destroy();
        });
    }
  );

  window.HFDSAccordion = Object.freeze({
    version: "1.0.0",
    initialize: initializeAccordions
  });
})();